import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@copro/api';
import { subDays } from 'date-fns';
import { useSpace } from '../../../hooks/useSpace';
import type { AiConversation } from '../types/aiChat.types';
import { keys } from './keys';

// Conversations are kept one month (purged by the `ai-chat` edge function).
export const CONVERSATION_RETENTION_DAYS = 30;
const MAX_CONVERSATIONS = 50;

export const CONVERSATION_SELECT = 'id, title, last_message_preview, updated_at, created_by, profiles (first_name, last_name)';

type ConversationRow = {
  id: string;
  title: string;
  last_message_preview: string | null;
  updated_at: string;
  created_by: string | null;
  profiles: { first_name: string | null; last_name: string | null } | null;
};

export function toAiConversation(row: ConversationRow): AiConversation {
  return {
    id: row.id,
    title: row.title,
    lastMessagePreview: row.last_message_preview,
    updatedAt: row.updated_at,
    createdBy: row.created_by,
    authorFirstName: row.profiles?.first_name ?? null,
    authorLastName: row.profiles?.last_name ?? null,
  };
}

async function fetchAiConversations(spaceId: string): Promise<AiConversation[]> {
  const { data, error } = await apiClient
    .from('ai_conversations')
    .select(CONVERSATION_SELECT)
    .eq('space_id', spaceId)
    .gte('updated_at', subDays(new Date(), CONVERSATION_RETENTION_DAYS).toISOString())
    .order('updated_at', { ascending: false })
    .limit(MAX_CONVERSATIONS);

  if (error) {
    throw error;
  }

  return (data ?? []).map(toAiConversation);
}

export function useGetAiConversations() {
  const { currentSpace } = useSpace();

  return useQuery({
    queryKey: keys.conversations(currentSpace?.id ?? ''),
    queryFn: () => fetchAiConversations(currentSpace!.id),
    enabled: !!currentSpace?.id,
  });
}
